import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';

import {
  CredentialClient,
  ContentFormat,
  CredentialSignerType,
  SparseCredential,
  ContentItem,
  StoreCredential,
} from '@civic/credential-client';
import { ScopeRequest, schemaLoader } from '@civic/dsr';
import * as R from 'ramda';
import { CustomError, TechnicalError } from '../common/errors';
import Result from '../common/result';
import { useAuthentication } from './AuthenticationContext';
import { useSigner } from './SignerContext';
import { Resolver } from '../config';
import { Credentials, Evidence } from '../common/types';
import {
  EmailCredentialSubject,
  IDDocumentCredentialSubject,
  VerifiableCredential,
  VerifiableCredentialExtended,
  DecryptionStatus,
  ReuseStatus,
  StoreStatus,
} from '../common/types/credentials';
import EvidenceService from '../services/credential/evidenceService';
import { useGKNService } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { MemoryRouterContext } from './MemoryRouterContext';
import { useScopeRequest } from './ScopeRequestContext';
import Log from '../common/logging';
import SchemaLoader from '../services/credential/schemaService';
import schemaMapping from '../utils/jsonSchemaLoader';
import { useFlowConfig } from './FlowConfigContext';

export interface CredentialStoreContextProps {
  setStoreStatus: (storeStatus: StoreStatus) => void;
  storeStatus: StoreStatus;
  allCredentialsStored: () => Promise<boolean>;
  retrieveExistingInProgress: boolean;
  credentialClientInst?: CredentialClient | null;
  storeCredentials: (
    retrievedCredentials: Credentials,
    abortController?: AbortController,
  ) => Promise<Result<CustomError>>;
  decryptCredentials: (ids: string[]) => Promise<Result<TechnicalError, StoreCredential[]>>;
  decryptStatus?: DecryptionStatus;
  getExistingRequiredCredentialIds: () => Promise<string[] | null>;
  setDecryptStatus: (status: DecryptionStatus) => void;
  setReuseStatus: (status: ReuseStatus) => void;
  reuseStatus?: ReuseStatus;
  decryptedCredentials?: StoreCredential[];
  civicCredentials?: VerifiableCredential[];
  scopeRequestCredentials?: Credentials | undefined;
  chosenCredentialIds: string[];
  setChosenCredentialIds: (ids: string[]) => void;
  existingStoredCredentials?: SparseCredential[];
  getDocumentCredentials: () => Promise<VerifiableCredentialExtended<IDDocumentCredentialSubject>[]>;
  emailCredentials: VerifiableCredential<EmailCredentialSubject>[] | undefined;
}

export const CredentialStoreContext = createContext<CredentialStoreContextProps>({} as CredentialStoreContextProps);

type CredentialStoreProps = {
  children: JSX.Element;
};

/**
 * Provider to fetch and resolve scope request with credentials
 */
export function CredentialStoreProvider(props: CredentialStoreProps): JSX.Element {
  const { children } = props;
  const { setCredentialReuse } = useFlowConfig();
  const { credentialStorageEnabled } = useGKNService();
  const { queryParams } = useContext(MemoryRouterContext);
  const [storeStatus, setStoreStatus] = useState<StoreStatus>(StoreStatus.pending);
  const { getCurrentToken, user, loggedIn } = useAuthentication();
  const { signer } = useSigner();
  const serviceConfig = Resolver.getServiceConfig();
  const [credentialClientInst, setCredentialClientInst] = useState<CredentialClient | null>(null); // State to hold the credential client instance
  const [existingStoredCredentials, setExistingStoredCredentials] = useState<SparseCredential[] | undefined>(undefined); // State to hold the existing credentials
  const [retrieveExistingInProgress, setRetrieveExistingInProgress] = useState<boolean>(false);
  const [decryptStatus, setDecryptStatus] = useState<DecryptionStatus>();
  const [reuseStatus, setReuseStatus] = useState<ReuseStatus>();
  const { action } = queryParams || {};
  const { currentRequiredCredentials, currentRequiredEvidences, scopeRequest: currentScopeRequest } = useScopeRequest();
  const [decryptedCredentials, setDecryptedCredentials] = useState<StoreCredential[] | undefined>(undefined);
  const [chosenCredentialIds, setChosenCredentialIds] = useState<string[]>([]);

  // load the scope request schemas so they are ready for use in validation if necessary
  schemaLoader.addLoader(new SchemaLoader(schemaMapping));

  const decryptCredentials = useCallback(
    async (ids: string[]): Promise<Result<TechnicalError, StoreCredential[]>> => {
      setDecryptStatus(DecryptionStatus.inProgress);
      const decryptPromises = ids.map(async (id) => {
        try {
          const credential = await credentialClientInst?.get(id);
          if (!credential) {
            throw Error('Error decrypting credential');
          }
          return credential;
        } catch (error) {
          Log.error(`Error decrypting credential ${id}`, error as Error);
          throw Error('Error decrypting credential');
        }
      });
      try {
        const decryptedCredentialsToStore = await Promise.all(decryptPromises);
        setDecryptStatus(DecryptionStatus.complete);
        setDecryptedCredentials(decryptedCredentialsToStore);
        return Result.successful(decryptedCredentialsToStore);
      } catch (error) {
        setDecryptStatus(DecryptionStatus.failed);
        return Result.failed(new TechnicalError('Error decrypting credentials'));
      }
    },
    [credentialClientInst],
  );

  const allCredentialsStored = useCallback(async () => {
    if (!credentialStorageEnabled) {
      return false;
    }
    const requiredCredentials = await currentRequiredCredentials();
    if (existingStoredCredentials && existingStoredCredentials?.length > 0) {
      const allNames = existingStoredCredentials?.map((cred) => cred.tags).flat();
      const allNamesContainRequiredCredentials = requiredCredentials.every((credential) => {
        const nameInCredential = allNames?.find((tag) => {
          return tag.includes(credential);
        });
        return nameInCredential;
      });
      return allNamesContainRequiredCredentials;
    }
    return false;
  }, [credentialStorageEnabled, currentRequiredCredentials, existingStoredCredentials]);

  const getExistingRequiredCredentialIds = useCallback(async (): Promise<string[] | null> => {
    const areAllCredentialsStored = await allCredentialsStored();
    if (!areAllCredentialsStored) {
      return null;
    }
    const requiredCredentials = await currentRequiredCredentials();
    const existingRequiredCredentialIds = existingStoredCredentials
      ?.filter((cred) => cred.tags.some((tag) => requiredCredentials.includes(tag)))
      .map((cred) => cred.credentialId);

    if (!existingRequiredCredentialIds) {
      throw new TechnicalError('Error getting existing required credential ids');
    }
    return existingRequiredCredentialIds;
  }, [allCredentialsStored, currentRequiredCredentials, existingStoredCredentials]);

  const fetchExistingStoredCredentials = useCallback(
    async (abortController?: AbortController, override?: false): Promise<Result<CustomError, SparseCredential[]>> => {
      if (existingStoredCredentials && existingStoredCredentials?.length > 0 && !override) {
        return Result.successful(existingStoredCredentials);
      }
      if (!credentialClientInst) {
        return Result.failed(new TechnicalError('Unexpected error creating credential storage client'));
      }
      const existingCredentials = await credentialClientInst.find([]);
      if (abortController?.signal.aborted) {
        return Result.successful([]);
      }
      setExistingStoredCredentials(existingCredentials);
      return Result.successful(existingCredentials);
    },
    [credentialClientInst, existingStoredCredentials],
  );

  const getCredentialRequestContent = (credential: VerifiableCredential) => ({
    name: credential.identifier,
    mimeType: 'application/json',
    blob: new Uint8Array(Buffer.from(JSON.stringify(credential))),
    format: 'JWE' as ContentFormat,
  });

  const getCredentialRequest = (credential: VerifiableCredential, evidenceTags: string[], content: ContentItem[]) => ({
    credentialId: credential.id,
    tags: [credential.identifier, ...evidenceTags],
    content,
    metadata: {
      issuanceDate: credential.issuanceDate,
      ...(credential?.expirationDate ? { expirationDate: credential.expirationDate } : {}),
    },
  });

  const storeCredentials = useCallback(
    async (retrievedCredentials: Credentials, abortController?: AbortController): Promise<Result<CustomError>> => {
      if (!credentialClientInst?.did) {
        return Result.failed(new TechnicalError('Unexpected error: no credential storage client'));
      }
      setStoreStatus(StoreStatus.inProgress);
      try {
        const primaryCredential = retrievedCredentials.credentials?.find(
          (cred) => cred.identifier === 'credential-cvc:IdDocument-v3',
        );
        if (!primaryCredential) {
          setStoreStatus(StoreStatus.failed);
          return Result.failed(new TechnicalError('No ID Document credential found'));
        }
        const evidenceTags = retrievedCredentials.evidences?.map((evidence) => evidence.content) || [];
        const primaryContent = getCredentialRequestContent(primaryCredential);
        const evidenceService = EvidenceService.getInstance();
        const evidencePromises =
          retrievedCredentials.evidences?.map(async (evidence): Promise<ContentItem> => {
            const evidenceImg = await evidenceService.downloadEvidence(evidence);
            if (abortController?.signal.aborted) {
              return Promise.reject(new Error('Aborted'));
            }
            return {
              name: evidence.content,
              mimeType: evidence['content-type'],
              blob: new Uint8Array(Buffer.from(evidenceImg.evidenceData)),
              format: 'JWE' as ContentFormat,
            };
          }) || [];
        const evidenceContent = await Promise.all(evidencePromises);
        if (abortController?.signal.aborted) {
          return Promise.reject(new Error('Aborted'));
        }
        const primaryStoreRequest = getCredentialRequest(
          primaryCredential,
          [...evidenceTags, primaryCredential.identifier],
          [...evidenceContent, primaryContent],
        );
        const otherCredentials = retrievedCredentials.credentials?.filter(
          (cred) => cred.identifier !== 'credential-cvc:IdDocument-v3',
        );
        const otherCredentialRequest = (credential: VerifiableCredential) =>
          getCredentialRequest(credential, [credential.identifier], [getCredentialRequestContent(credential)]);
        await credentialClientInst.store([primaryStoreRequest, ...otherCredentials.map(otherCredentialRequest)]);
        if (abortController?.signal.aborted) {
          return Promise.reject(new Error('Aborted'));
        }
        setStoreStatus(StoreStatus.complete);
        return Result.noValueSuccess();
      } catch (error) {
        setStoreStatus(StoreStatus.failed);
        return Result.failed(new TechnicalError('Error during credential storage'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [credentialClientInst?.did],
  );

  useEffect(() => {
    if (!credentialStorageEnabled) {
      return;
    }
    const abortController = new AbortController();
    const getCredentialClient = async (): Promise<CredentialClient | null> => {
      const token = await getCurrentToken();
      if (abortController.signal.aborted) {
        return null;
      }
      if (token.isFailed()) {
        token.logError('Error getting token for credential store context');
        return null;
      }
      const auth = token.success();
      const did = user?.did;
      if (!did || !auth || !signer) {
        return null;
      }

      return CredentialClient.create(did, {
        identityStoreEndpoint: serviceConfig.identityStore.endpoint,
        auth,
        signer: {
          signMessage: (message: Uint8Array) => signer.signMessage(message).then((signature) => signature.success()),
          type: CredentialSignerType.WALLET,
        },
      });
    };
    if (loggedIn && user?.did) {
      getCredentialClient().then(setCredentialClientInst);
    }
  }, [user?.did, getCurrentToken, loggedIn, signer, serviceConfig, credentialStorageEnabled]);

  useEffect(() => {
    // During mobile handover flow, we don't want to fetch existing credentials
    if (!credentialStorageEnabled || action === 'link') {
      return;
    }
    if (credentialClientInst?.did && !retrieveExistingInProgress && !existingStoredCredentials) {
      setRetrieveExistingInProgress(true); // Prevent multiple calls to fetchExistingStoredCredentials(
      fetchExistingStoredCredentials().finally(() => {
        setRetrieveExistingInProgress(false);
      });
    }
  }, [
    credentialClientInst?.did,
    retrieveExistingInProgress,
    existingStoredCredentials,
    fetchExistingStoredCredentials,
    action,
    credentialStorageEnabled,
  ]);

  useEffect(() => {
    if (!credentialStorageEnabled) {
      return;
    }
    if (!user?.did) {
      setExistingStoredCredentials(undefined);
      setCredentialClientInst(null);
      setRetrieveExistingInProgress(false);
    }
  }, [credentialStorageEnabled, user?.did]);

  const civicCredentials = useMemo((): VerifiableCredential[] | undefined => {
    if (decryptedCredentials) {
      try {
        return decryptedCredentials.map((cred) => {
          const content = cred.content.find(
            (item) => item.name.includes('credential') && item.mimeType === 'application/json',
          );
          if (!content) {
            return null;
          }
          return JSON.parse(Buffer.from(content.blob).toString()) as VerifiableCredential;
        }) as VerifiableCredential[];
      } catch (error) {
        Log.error('Error parsing civic credentials', error as Error);
        return undefined;
      }
    }
    return undefined;
  }, [decryptedCredentials]);

  const evidencesForChosenCredentials = useMemo((): Evidence[] => {
    if (decryptedCredentials) {
      const chosenStoredCredentials = decryptedCredentials.filter((storeCred) =>
        chosenCredentialIds.includes(storeCred.credentialId),
      );
      const evidences = chosenStoredCredentials
        .map((storeCred) => {
          const evidenceContent = storeCred?.content.filter((item) => currentRequiredEvidences.includes(item.name));
          return evidenceContent?.map((content) => {
            return {
              content: content.name,
              'content-type': content.mimeType,
              blob: content.blob,
              sha256: content.sha256,
            } as Evidence;
          });
        })
        .flat(); // flatten all evidences into a single array

      return evidences || [];
    }
    return [];
  }, [decryptedCredentials, chosenCredentialIds, currentRequiredEvidences]);

  const scopeRequestCredentials = useMemo((): Credentials | undefined => {
    if (civicCredentials && civicCredentials.length > 0 && chosenCredentialIds.length > 0) {
      return {
        credentials: civicCredentials.filter((cred) => chosenCredentialIds.includes(cred.id)),
        evidences: evidencesForChosenCredentials,
      };
    }
    return undefined;
  }, [civicCredentials, evidencesForChosenCredentials, chosenCredentialIds]);

  const emailCredentials = useMemo((): VerifiableCredential<EmailCredentialSubject>[] => {
    return (civicCredentials?.filter((credential) => credential.identifier.includes('credential-cvc:Email-v3')) ||
      []) as VerifiableCredential<EmailCredentialSubject>[];
  }, [civicCredentials]);

  const validateCredentialAgainstDSR = useCallback(
    async (documentCredential: VerifiableCredential<IDDocumentCredentialSubject>) => {
      const dsr = currentScopeRequest?.components.identity.request?.payload;
      const isValid = await ScopeRequest.ScopeRequest.credentialsMatchesRequest(
        [...emailCredentials, documentCredential],
        dsr,
        true,
      );
      if (isValid) {
        return {
          isValid,
          failedConstraints: [],
        };
      }
      const failedConstraints = await ScopeRequest.ScopeRequest.getFailingConstraints(
        [...emailCredentials, documentCredential],
        dsr,
        true,
      );
      Log.debug('Failed claims:', { failedConstraints });
      return {
        isValid,
        failedConstraints,
      };
    },
    [currentScopeRequest, emailCredentials],
  );

  /**
   * return the document credentisl, validating each against the scope request DSR
   * and adding a validation object to each credential
   */
  const getDocumentCredentials = useCallback(async (): Promise<
    VerifiableCredentialExtended<IDDocumentCredentialSubject>[]
  > => {
    const extendCredential = async (
      cred: VerifiableCredential<IDDocumentCredentialSubject>,
    ): Promise<VerifiableCredentialExtended<IDDocumentCredentialSubject>> => ({
      ...cred,
      validation: await validateCredentialAgainstDSR(cred),
    });
    const documentPromises = civicCredentials
      ?.filter((credential) => credential.identifier.includes('credential-cvc:IdDocument'))
      .map((cred) => extendCredential(cred as VerifiableCredential<IDDocumentCredentialSubject>));

    if (!documentPromises) {
      return [];
    }
    // sort the responses with the valid credentials first
    const unsorted = await Promise.all(documentPromises);
    return R.sortBy((doc: VerifiableCredentialExtended) => !!doc.validation?.isValid)(
      unsorted,
    ).reverse() as VerifiableCredentialExtended<IDDocumentCredentialSubject>[];
  }, [civicCredentials, validateCredentialAgainstDSR]);

  useEffect(() => {
    setCredentialReuse({ credentialIds: chosenCredentialIds, status: reuseStatus });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenCredentialIds.length, reuseStatus, setCredentialReuse]);

  const value = useMemo(
    () => ({
      storeStatus,
      setStoreStatus,
      allCredentialsStored,
      retrieveExistingInProgress,
      credentialClientInst,
      storeCredentials,
      decryptCredentials,
      decryptStatus,
      getExistingRequiredCredentialIds,
      setDecryptStatus,
      setReuseStatus,
      reuseStatus,
      decryptedCredentials,
      civicCredentials,
      scopeRequestCredentials,
      chosenCredentialIds,
      setChosenCredentialIds,
      existingStoredCredentials,
      getDocumentCredentials,
      emailCredentials,
    }),
    [
      storeStatus,
      allCredentialsStored,
      retrieveExistingInProgress,
      credentialClientInst,
      storeCredentials,
      decryptCredentials,
      decryptStatus,
      getExistingRequiredCredentialIds,
      setDecryptStatus,
      setReuseStatus,
      reuseStatus,
      decryptedCredentials,
      civicCredentials,
      scopeRequestCredentials,
      chosenCredentialIds,
      setChosenCredentialIds,
      existingStoredCredentials,
      getDocumentCredentials,
      emailCredentials,
    ],
  );

  return <CredentialStoreContext.Provider value={value}>{children}</CredentialStoreContext.Provider>;
}
export const useCredentialStore = (): CredentialStoreContextProps => useContext(CredentialStoreContext);
